import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './agenda.css';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid
} from '@material-ui/core';
import {
  Add as AddIcon,
  FilterList as FilterIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import api from '../../services/api';
import AgendamentoModal from '../../components/AgendamentoModal';
import AgendamentoDetalhes from '../../components/AgendamentoDetalhes';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { canShowMenu } from '../../helpers/menuAccess';
import { getLocaleCode } from '../../translate/locale';
import { isDarkTheme } from '../../helpers/darkTint';

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  calendar: {
    height: 'calc(100vh - 250px)',
    '& .rbc-event': {
      borderRadius: 4,
      padding: '2px 5px',
    },
    '& .rbc-today': {
      backgroundColor: theme.palette.type === 'dark' ? '#0d3049 !important' : '#e3f2fd !important',
    },
    '& .rbc-off-range-bg': {
      backgroundColor: theme.palette.type === 'dark' ? '#222222' : '#fafafa',
    },
    '& .rbc-off-range .rbc-button-link': {
      color: theme.palette.type === 'dark' ? '#555555' : undefined,
    },
    '& .rbc-header': {
      padding: '10px 3px',
      fontWeight: 500,
      fontSize: '0.9rem',
      color: theme.palette.type === 'dark' ? '#e0e0e0' : undefined,
      borderBottom: theme.palette.type === 'dark' ? '2px solid #888888' : undefined,
    },
    '& .rbc-month-view': {
      border: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
    '& .rbc-month-row': {
      borderTop: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
    '& .rbc-day-bg + .rbc-day-bg': {
      borderLeft: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
    '& .rbc-day-bg:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f5f5f5',
    },
    '& .rbc-toolbar button': {
      color: theme.palette.type === 'dark' ? '#e0e0e0' : theme.palette.primary.main,
      border: theme.palette.type === 'dark' ? '1px solid #555555' : `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.type === 'dark' ? '#3a3a3a' : 'white',
      padding: '5px 10px',
      margin: '0 2px',
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? '#4a4a4a' : theme.palette.action.hover,
        borderColor: theme.palette.type === 'dark' ? '#777777' : undefined,
      },
      '&.rbc-active': {
        backgroundColor: theme.palette.type === 'dark' ? '#37474f' : theme.palette.primary.main,
        color: '#ffffff',
        borderColor: theme.palette.type === 'dark' ? '#607d8b' : undefined,
      },
    },
    '& .rbc-show-more': {
      color: theme.palette.type === 'dark' ? '#90caf9' : '#1976d2',
    },
    '& .rbc-agenda-view': {
      border: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
    '& .rbc-time-header-content': {
      borderLeft: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
    '& .rbc-time-content': {
      borderTop: theme.palette.type === 'dark' ? '2px solid #444444' : undefined,
    },
    '& .rbc-timeslot-group': {
      borderBottom: theme.palette.type === 'dark' ? '1px solid #888888' : undefined,
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

const Agenda = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const history = useHistory();
  const location = useLocation();
  const { user, menus } = useContext(AuthContext);
  const [agendamentos, setAgendamentos] = useState([]);
  const [selectedAgendamento, setSelectedAgendamento] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detalhesOpen, setDetalhesOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    userId: user?.id || '',
  });
  const [users, setUsers] = useState([]);

  const canViewFollowupUserFilter = useMemo(
    () => canShowMenu(user, menus, 'followup_filter_user'),
    [menus, user]
  );

  const effectiveUserIdFilter = canViewFollowupUserFilter
    ? filters.userId
    : (user?.id || '');

  useEffect(() => {
    if (!user?.id) return;

    // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: manter usuário logado selecionado por padrão no filtro de follow-up.
    setFilters((prevFilters) => {
      if (prevFilters.userId) return prevFilters;
      return { ...prevFilters, userId: user.id };
    });
  }, [user?.id]);

  useEffect(() => {
    if (canViewFollowupUserFilter || !user?.id) return;

    // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: quando nao pode ver o filtro, manter listagem restrita ao proprio usuario.
    setFilters((prevFilters) => ({
      ...prevFilters,
      userId: user.id,
    }));
  }, [canViewFollowupUserFilter, user?.id]);

  useEffect(() => {
    loadAgendamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, effectiveUserIdFilter]);

  useEffect(() => {
    if (location.state?.openNewFollowup) {
      setSelectedAgendamento({ tipo: 'followup' });
      setDetalhesOpen(false);
      setModalOpen(true);
      history.replace('/followup');
    }
  }, [history, location.state]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        // Autor: GitHub Copilot | Data: 26/03/2026 | Motivo: carregar usuários para permitir filtro de follow-up por responsável.
        const { data } = await api.get('/users');
        setUsers(data.users || []);
      } catch (error) {
        toastError(error);
      }
    };

    loadUsers();
  }, []);

  const loadAgendamentos = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (effectiveUserIdFilter) params.userId = effectiveUserIdFilter;

      const { data } = await api.get('/agendamentos', { params });
      
      const events = data.agendamentos.map(ag => ({
        ...ag,
        title: ag.titulo,
        start: new Date(ag.dataInicio),
        end: new Date(ag.dataFim),
        resource: ag,
      }));
      
      setAgendamentos(events);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedAgendamento(event.resource);
    setDetalhesOpen(true);
  };

  const handleSelectSlot = ({ start, end }) => {
    setSelectedAgendamento({
      dataInicio: start,
      dataFim: end,
      // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: em /followup, novo registro deve nascer como follow-up.
      tipo: 'followup',
    });
    setModalOpen(true);
  };

  const eventStyleGetter = (event) => {
    const style = {
      borderRadius: '4px',
      opacity: 0.8,
      display: 'block',
      border: 'none',
    };

    switch (event.resource.status) {
      case 'pendente':
        style.backgroundColor = '#ff9800';
        break;
      case 'em_andamento':
        style.backgroundColor = '#2196f3';
        break;
      case 'concluido':
        style.backgroundColor = '#4caf50';
        break;
      case 'cancelado':
        style.backgroundColor = '#f44336';
        break;
      default:
        style.backgroundColor = '#757575';
    }

    return { style };
  };

  const handleSave = () => {
    loadAgendamentos();
    setModalOpen(false);
    setSelectedAgendamento(null);
  };

  const handleDelete = () => {
    loadAgendamentos();
    setDetalhesOpen(false);
    setSelectedAgendamento(null);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Agenda</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: evitar criação como reunião ao abrir "Novo Follow-up".
              setSelectedAgendamento({ tipo: 'followup' });
              setModalOpen(true);
            }}
          >
            Novo Follow-up
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <Grid container spacing={2} className={classes.toolbar}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pendente">Pendente</MenuItem>
                <MenuItem value="em_andamento">Em Andamento</MenuItem>
                <MenuItem value="concluido">Concluído</MenuItem>
                <MenuItem value="cancelado">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* Data: 20/04/2026 | Motivo: ocultar filtro de tipo na agenda de follow-up; backend passa a fixar follow-up. */}
          {/**
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={filters.tipo}
                onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="reuniao">Reunião</MenuItem>
                <MenuItem value="ligacao">Ligação</MenuItem>
                <MenuItem value="visita">Visita</MenuItem>
                <MenuItem value="followup">Follow-up</MenuItem>
                <MenuItem value="tarefa">Tarefa</MenuItem>
                <MenuItem value="outro">Outro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          */}
          {canViewFollowupUserFilter && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Usuário</InputLabel>
                <Select
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                  label="Usuário"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={12} md={canViewFollowupUserFilter ? 3 : 6}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Chip
                label="Pendente"
                size="small"
                style={{ backgroundColor: '#ff9800', color: '#fff' }}
              />
              <Chip
                label="Em Andamento"
                size="small"
                style={{ backgroundColor: '#2196f3', color: '#fff' }}
              />
              <Chip
                label="Concluído"
                size="small"
                style={{ backgroundColor: '#4caf50', color: '#fff' }}
              />
              <Chip
                label="Cancelado"
                size="small"
                style={{ backgroundColor: '#f44336', color: '#fff' }}
              />
            </div>
          </Grid>
        </Grid>

        <Calendar
          localizer={localizer}
          events={agendamentos}
          startAccessor="start"
          endAccessor="end"
          // agenda-dark: bordas e fundos do CSS padrão do react-big-calendar no tema escuro (agenda.css)
          className={isDark ? `${classes.calendar} agenda-dark` : classes.calendar}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          eventPropGetter={eventStyleGetter}
          messages={{
            next: 'Próximo',
            previous: 'Anterior',
            today: 'Hoje',
            month: 'Mês',
            week: 'Semana',
            day: 'Dia',
            agenda: 'Agenda',
            date: 'Data',
            time: 'Hora',
            event: 'Evento',
            noEventsInRange: 'Não há eventos neste período.',
            showMore: (total) => `+ Ver mais (${total})`,
            allDay: 'Dia inteiro',
          }}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
          step={30}
          showMultiDayTimes
          culture={getLocaleCode()}
        />
      </Paper>

      <AgendamentoModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedAgendamento(null);
        }}
        agendamento={selectedAgendamento}
        forceFollowupType
        onSave={handleSave}
      />

      <AgendamentoDetalhes
        open={detalhesOpen}
        onClose={() => {
          setDetalhesOpen(false);
          setSelectedAgendamento(null);
        }}
        agendamento={selectedAgendamento}
        onEdit={() => {
          setDetalhesOpen(false);
          setModalOpen(true);
        }}
        onDelete={handleDelete}
        onConclude={() => {
          loadAgendamentos();
        }}
      />
    </MainContainer>
  );
};

export default Agenda;
